import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import main_image from "./course.jpg";
import gbflag from "./gbflag.png";
import { RezervovatButton } from "../Main/MainPage";
import { Link, useNavigate } from "react-router-dom";
import { Motion } from "../../Components/Motion";
import logo from '../../Pages/Main/logo.png';
import { EditableTypography } from "../../Components/EditableTypography";
import EditableImage from "../../Components/EditableImage";
import { useFirestore } from "../../Firebase/FirestoreProvider";
import { useAuth } from "../../Firebase/AuthProvider";

const BREAKPOINT = 900;

const PHOTO_SRC = main_image;

const LOGO_SRC = logo;

const C = {
    red: "#E0342A",
    redDark: "#C42A22",
    pink: "#E8558E",
    navy: "#14539A",
    navyDark: "#0F4485",
    blueLine: "#2F6FE0",
    ink: "#2B2B2B",
    paper: "#FFFFFF",
    page: "#EFF2F6",
    green: "#2E8B57",
    greenDark: "#1F6B41",
    amber: "#B4562A",
    gold: "#9A6A12",
};

const FONT =
    '"Nunito", "Segoe UI", "Trebuchet MS", system-ui, -apple-system, sans-serif';

const FONT_SIMPLE = "Roboto";

function GBFlag({ sx = {} }) {
    return (
        <Box
            component="img"
            src={gbflag}
            alt="EN"
            sx={{ width: "25px", height: "auto", ...sx }}
        />
    );
}

/* ------------------------------------------------------------------
   Malé stavební bloky
------------------------------------------------------------------ */

function Card({ bg, border, children, sx = {}, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                backgroundColor: bg,
                border: `2px solid ${border}`,
                borderRadius: "16px",
                p: 2,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

function SectionTitle({ color, size = 20, children, fireKey, sx = {} }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, ...sx }}>
            <EditableTypography
                fireKey={fireKey}
                component="h3"
                sx={{
                    fontFamily: FONT,
                    fontWeight: 900,
                    fontSize: size,
                    color,
                    lineHeight: 1.2,
                    textAlign: "left",
                }}
            />
        </Box>
    );
}

function Text({ fireKey, sx = {} }) {
    return (
        <EditableTypography
            fireKey={fireKey}
            sx={{
                fontFamily: FONT,
                fontSize: 15,
                lineHeight: 1.55,
                color: C.ink,
                textAlign: "left",
                ...sx,
            }}
        />
    );
}

const B = ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 800 }}>
        {children}
    </Box>
);

/* Dlaždice "Jak se děti učí?" – ikona vlevo, popisek vpravo */
function Tile({ fireKey, fireKeyIcon, bg, border, labelColor }) {
    return (
        <Box
            sx={{
                backgroundColor: bg,
                border: `2px solid ${border}`,
                borderRadius: "14px",
                px: 1.25,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 68,
            }}
        >
            <EditableTypography
                fireKey={fireKeyIcon}
                sx={{ fontSize: 22, lineHeight: 1, flex: "0 0 auto", color: labelColor, }} />

            <EditableTypography
                fireKey={fireKey}
                sx={{
                    fontFamily: FONT,
                    fontWeight: 800,
                    fontSize: 13.5,
                    color: labelColor,
                    lineHeight: 1.25,
                    textAlign: "left",
                }}
            />
        </Box>
    );
}

function CheckItem({ children, sx = {} }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.9, py: 0.3, ...sx }}>
            <Box
                sx={{
                    flex: "0 0 auto",
                    width: 18,
                    height: 18,
                    mt: "1px",
                    borderRadius: "50%",
                    backgroundColor: "#3FA94C",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 900,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                ✓
            </Box>
            <Typography
                sx={{
                    fontFamily: FONT,
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#1F6B2C",
                    lineHeight: 1.35,
                    textAlign: "left",
                    mt: '1px'
                }}
            >
                {children}
            </Typography>
        </Box>
    );
}

/* Logo vpravo nahoře */
function Logo({ column }) {
    if (LOGO_SRC) {
        return (
            <Box
                component="img"
                src={LOGO_SRC}
                alt="Hello Kids Camp"
                sx={{ width: column ? 120 : 150, height: "auto", display: "block" }}
            />
        );
    }
    return (
        <Box sx={{ textAlign: "center", lineHeight: 1 }}>
            <Box sx={{ fontSize: column ? 26 : 32, mb: 0.25 }}>🧒👧</Box>
            <Typography
                sx={{
                    fontFamily: FONT,
                    fontWeight: 900,
                    fontSize: column ? 16 : 19,
                    letterSpacing: 0.5,
                    color: "#F0A431",
                    lineHeight: 1.05,
                }}
            >
                HELLO{" "}
                <Box component="span" sx={{ color: C.pink }}>
                    KIDS
                </Box>
            </Typography>
            <Typography
                sx={{
                    fontFamily: FONT,
                    fontWeight: 900,
                    fontSize: column ? 20 : 24,
                    letterSpacing: 1,
                    color: "#F0A431",
                    lineHeight: 1.05,
                }}
            >
                CAMP
            </Typography>
        </Box>
    );
}

const Checks = () => {
    const { isAdmin } = useAuth();
    const { getData } = useFirestore();
    const checks = getData("kcheckslist");
    if (!checks || typeof checks !== "string") return <></>;
    const list = checks.split(";");
    if (!list || list.length <= 1) return <></>;
    return <>
        {list.map((d, i) => <CheckItem key={"CIT" + i}>{String(d).trim()}</CheckItem>)}
        {isAdmin && <EditableTypography fireKey={"kcheckslist"} modifyVisible={() => null} />}
    </>;
}

/* ------------------------------------------------------------------
   Stránka
------------------------------------------------------------------ */

export default function CoursesMain() {
    const navigate = useNavigate();
    const column = useMediaQuery(`(max-width:${BREAKPOINT}px)`);

    React.useEffect(() => {
        const id = "nunito-font-link";
        if (document.getElementById(id)) return;
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href =
            "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap";
        document.head.appendChild(link);
    }, []);

    const goToReservation = () => navigate("/kurzy_rezervace");

    return (
        <Box
            id="course"
            sx={{
                backgroundColor: C.page,
                minHeight: "100vh",
                pt: "180px",
                pb: { xs: 2, md: 5 },
                px: { xs: 1.5, md: 3 },
                fontFamily: FONT,
            }}
        >
            {/* list papíru */}
            <Box
                sx={{
                    maxWidth: 1150,
                    mx: "auto",
                    backgroundColor: C.paper,
                    borderRadius: "22px",
                    overflow: "hidden",
                    boxShadow: "0 10px 30px rgba(20,40,80,.10)",
                }}
            >
                {/* ---------------- Hlavička ---------------- */}
                <Box
                    sx={{
                        p: { xs: 2, md: 3 },
                        pb: { xs: 1.5, md: 2 },
                        display: "grid",
                        gridTemplateColumns: column ? "1fr" : "1fr auto",
                        gap: 2,
                        alignItems: "center",
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                backgroundColor: C.red,
                                borderRadius: "14px",
                                px: { xs: 2, md: 3 },
                                py: 1,
                                display: "block",
                            }}
                        >
                            <EditableTypography
                                fireKey="kurz_h1"
                                component="h1"
                                sx={{
                                    fontFamily: FONT_SIMPLE,
                                    fontWeight: 800,
                                    color: "#fff",
                                    fontSize: column ? 30 : 46,
                                    letterSpacing: 0.5,
                                    lineHeight: 1.1,
                                    textAlign: "left",
                                    mt: '4px'
                                }}
                            />
                        </Box>

                        <EditableTypography
                            fireKey="kurz_h2"
                            component="h2"
                            sx={{
                                fontFamily: FONT_SIMPLE,
                                fontWeight: 900,
                                color: C.navy,
                                fontSize: column ? 24 : 40,
                                letterSpacing: 0.5,
                                lineHeight: 1.15,
                                mt: 1.7,
                                mb: 1.25,
                                textAlign: "left",
                            }}
                        />

                        <Box
                            sx={{
                                display: "inline-block",
                                backgroundColor: C.pink,
                                borderRadius: 999,
                                px: 3,
                                py: 0.9,
                                maxWidth: "100%",
                                width: '100%',
                            }}
                        >
                            <EditableTypography
                                fireKey="kurz_h3"
                                sx={{
                                    fontFamily: FONT_SIMPLE,
                                    fontWeight: 800,
                                    fontSize: column ? 15 : 19,
                                    color: "#FFF",
                                    lineHeight: 1.3,
                                    textAlign: 'left',
                                }}
                            />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            justifySelf: column ? "center" : "end",
                            backgroundColor: "#F7FAFF",
                            border: "2px solid #E1EAF7",
                            borderRadius: "16px",
                            px: 2,
                            py: 1.25,
                            ...(column && { width: '100%', display: 'flex', justifyContent: 'center' })
                        }}
                    >
                        <Logo column={column} />
                    </Box>
                </Box>

                {/* ---------------- Tělo: dva sloupce ---------------- */}
                <Box
                    sx={{
                        px: { xs: 2, md: 3 },
                        pb: { xs: 2, md: 3 },
                        display: "grid",
                        gridTemplateColumns: column ? "1fr" : "1fr 1fr",
                        gap: { xs: 2, md: 2.5 },
                        alignItems: "start",
                    }}
                >
                    {/* =============== LEVÝ SLOUPEC =============== */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, }}>
                        {/* foto */}
                        <EditableImage
                            fireKey={'kurz_imgmain'}
                            alt="Děti při hravé výuce angličtiny na koberci"
                            sx={{
                                width: "100%",
                                height: column ? 220 : 290,
                                objectFit: "cover",
                                borderRadius: "16px",
                                border: "3px solid #F0B93F",
                                display: "block",
                            }}
                        />

                        {/* Jak se děti učí? */}
                        <Box>
                            <SectionTitle fireKey="khowlearn" color={C.red} />
                            <Text fireKey='ktright' sx={{ mb: 1.25, fontSize: 14.5 }} />

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: column
                                        ? "repeat(2, 1fr)"
                                        : "repeat(3, 1fr)",
                                    gap: 1.25,
                                }}
                            >
                                <Tile
                                    fireKey={"kint1"}
                                    fireKeyIcon={"kinic1"}
                                    bg="#FDECF1"
                                    border="#F3BFD0"
                                    labelColor="#B03A63"
                                />
                                <Tile
                                    fireKey={"kint2"}
                                    fireKeyIcon={"kinic2"}
                                    bg="#FFF8E1"
                                    border="#F2D683"
                                    labelColor="#9A6A12"
                                />
                                <Tile
                                    fireKey={"kint3"}
                                    fireKeyIcon={"kinic3"}
                                    bg="#E9F7EE"
                                    border="#A9DCBB"
                                    labelColor="#1F6B41"
                                />
                                <Tile
                                    fireKey={"kint4"}
                                    fireKeyIcon={"kinic4"}
                                    bg="#E7F0FD"
                                    border="#AFCCF2"
                                    labelColor="#1A55A0"
                                />
                                <Tile
                                    fireKey={"kint5"}
                                    fireKeyIcon={"kinic5"}
                                    bg="#FDEDED"
                                    border="#F2BEBE"
                                    labelColor="#B33A3A"
                                />
                                <Tile
                                    fireKey={"kint6"}
                                    fireKeyIcon={"kinic6"}
                                    bg="#F3EDFC"
                                    border="#CDBAEE"
                                    labelColor="#5B3FA0"
                                />
                            </Box>
                        </Box>

                        {/* Přirozený jazyk */}
                        <Card bg="#EAF2FD" border="#B9D4F5">
                            <Box sx={{ display: "flex", gap: 1.25 }}>
                                <EditableTypography fireKey={'kmic2'} sx={{ fontSize: 20, lineHeight: 1.2 }} />
                                <Text fireKey={'ktprog'} sx={{ fontWeight: 700, color: C.navy }} />
                            </Box>
                        </Card>

                        {/* Co je součástí kurzu */}
                        <Card bg="#FFFFFF" border="#F0A431">
                            <SectionTitle fireKey="kwhatin" color={C.amber} size={19} />
                            <Text fireKey={'ktcourse'} />
                        </Card>

                        {/* Cena */}

                        <Card
                            bg="#FFFBEB"
                            border="#F0D585"
                        >
                            <SectionTitle fireKey="kpricetag" color={C.red} size={19} sx={{ mb: 0.5 }} />

                            <Box
                                sx={{
                                    display: "inline-block",
                                    textDecoration: "none",
                                    //backgroundColor: C.red,
                                    //color: "#fff",
                                    //borderRadius: "12px",
                                    //px: 2.5,
                                    width: '100%',
                                    py: 1,
                                    //mb: 1.25,
                                }}
                            >
                                <EditableTypography
                                    fireKey="kurz_price_per_year"
                                    sx={{
                                        fontFamily: FONT,
                                        fontWeight: 900,
                                        fontSize: column ? 22 : 27,
                                        whiteSpace: "nowrap",
                                        lineHeight: 1.15,
                                        color: C.red,
                                        textAlign: 'left',
                                    }}
                                />
                            </Box>

                            <Text fireKey={'ktpay'} sx={{ fontSize: 13.5, color: C.amber, fontWeight: 700 }} />
                            <Text fireKey={'ktpayp1'} sx={{ fontSize: 14, mt: 0.75 }} />
                            <Text fireKey={'ktpayp2'} sx={{ fontSize: 14 }} />
                        </Card>


                        {/* V ceně kurzu je */}
                        <Card bg="#F3FBF5" border="#A5D9BA">
                            <EditableTypography
                                fireKey="kurz_price_includes"
                                sx={{
                                    fontFamily: FONT,
                                    fontWeight: 900,
                                    fontSize: 19,
                                    color: C.greenDark,
                                    mb: 1,
                                    textAlign: "left",
                                }}
                            />
                            <Box
                                sx={{
                                    columnCount: column ? 1 : 2,
                                    columnGap: "16px",
                                    "& > *": {
                                        breakInside: "avoid",
                                        WebkitColumnBreakInside: "avoid",
                                        pageBreakInside: "avoid", // starší Safari
                                    },
                                }}
                            >
                                <Checks />

                            </Box>
                        </Card>
                    </Box>

                    {/* =============== PRAVÝ SLOUPEC =============== */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, }}>
                        <Card bg="#F5F9FF" border="#CFE0F5">
                            <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                                <EditableTypography fireKey={'kmic1'} sx={{ fontSize: 22, lineHeight: 1.1, mt: '2px' }} />
                                <EditableTypography
                                    fireKey="kurz_eng_fun"
                                    sx={{
                                        fontFamily: FONT,
                                        fontWeight: 900,
                                        fontSize: column ? 17 : 21,
                                        color: C.navy,
                                        lineHeight: 1.35,
                                        textAlign: "left",
                                    }}
                                />
                            </Box>
                        </Card>

                        <Card bg="#FFFDF3" border="#F0D585">
                            <SectionTitle fireKey={'kwhenwhere'} color={C.red} size={19} />
                            <EditableTypography
                                fireKey="kurz_x0"
                                sx={{
                                    fontFamily: FONT,
                                    fontWeight: 800,
                                    fontSize: column ? 16 : 19,
                                    color: C.navy,
                                    lineHeight: 1.45,
                                    textAlign: "left",
                                }}
                            />
                        </Card>

                        <Card bg="#EAF2FD" border="#B9D4F5">
                            <SectionTitle fireKey={'ksmallgroup'} color={C.navy} size={19} />
                            <Text fireKey={'kthowprog'} />
                        </Card>

                        <Card bg="#FFFBEB" border="#F0D585">
                            <SectionTitle fireKey={'kengpass'} color={C.gold} size={19} />
                            <Text fireKey={'ktlect'} />
                        </Card>

                        <Card bg="#EAF7EF" border="#A5D9BA">
                            <SectionTitle fireKey={'kwhoteach'} color={C.navy} size={19} />
                            <Text fireKey={'ktourlectors'} />
                            <Box
                                sx={{
                                    mt: 1.25,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Text fireKey={'ktincourse'} sx={{ fontWeight: 800 }} />
                                <GBFlag sx={{ flex: "0 0 auto" }} />
                            </Box>
                        </Card>

                        <Card bg="#FDEEF3" border="#F2C3D4">
                            <SectionTitle fireKey={'kparents'} color={C.pink} size={19} />
                            <Text fireKey={'ktwewant'} />
                        </Card>

                        <Motion scale={1.01} style={{ width: "100%" }}>
                            <Box component={Link}
                                to="/kurzy_rezervace"
                                sx={{ textDecoration: 'none' }}>
                                <Box
                                    sx={{
                                        backgroundColor: C.red,
                                        borderRadius: "14px",
                                        px: { xs: 2, md: 3 },
                                        py: 1,
                                        display: "block",
                                    }}
                                >
                                    <EditableTypography
                                        fireKey="kurz_h1_reserve"
                                        noPropagation
                                        component="h1"
                                        sx={{
                                            fontFamily: FONT_SIMPLE,
                                            fontWeight: 800,
                                            color: "#fff",
                                            fontSize: column ? 30 : 46,
                                            letterSpacing: 0.5,
                                            lineHeight: 1.1,
                                            textAlign: "center",
                                            mt: '4px',

                                        }}
                                    />
                                </Box></Box>
                        </Motion>
                    </Box>

                </Box>



                {/* ---------------- Patička s kontakty ---------------- */}
                <Box
                    sx={{
                        backgroundColor: C.navy,
                        px: { xs: 2, md: 3 },
                        py: 1.75,
                        display: "flex",
                        flexDirection: column ? "column" : "row",
                        alignItems: "center",
                        justifyContent: "space-around",
                        gap: column ? 1.25 : 2,
                    }}
                >
                    <Box
                        component="a"
                        href="https://www.hellokidscamp.cz"
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            textDecoration: "none",
                        }}
                    >
                        <Box sx={{ fontSize: 20 }}>🌐</Box>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontWeight: 800,
                                fontSize: column ? 15 : 17,
                                color: "#fff",
                            }}
                        >
                            www.hellokidscamp.cz
                        </Typography>
                    </Box>

                    <Box
                        component="a"
                        href="https://www.instagram.com/hellokidscamp/"
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            textDecoration: "none",
                        }}
                    >
                        <Box sx={{ fontSize: 20 }}>📷</Box>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontWeight: 800,
                                fontSize: column ? 15 : 17,
                                color: "#fff",
                                mt: '3px',
                            }}
                        >
                            hellokidscamp
                        </Typography>
                    </Box>

                    <Box
                        component="a"
                        href="tel:+420777120048"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            textDecoration: "none",
                        }}
                    >
                        <Box sx={{ fontSize: 20 }}>📞</Box>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontWeight: 800,
                                fontSize: column ? 15 : 17,
                                color: "#fff",
                            }}
                        >
                            777 120 048
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <RezervovatButton
                sx={{ pb: "80px" }}
                link="kurzy_rezervace"
                reverse
                sxTextBox={{ mt: "40px" }}
                sxButton={{ pt: "0px" }}
            />
        </Box>
    );
}
